import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import '../styles/SiteVisitsAnalytics.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const SiteVisitsAnalytics = () => {
  const [timeframe, setTimeframe] = useState('month');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Statistics data
  const [statistics, setStatistics] = useState(null);
  const [visitsByCountry, setVisitsByCountry] = useState([]);
  const [visitsByDevice, setVisitsByDevice] = useState([]);
  const [visitTimeline, setVisitTimeline] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [visitorSummary, setVisitorSummary] = useState([]);
  const [registeredUsersVisits, setRegisteredUsersVisits] = useState([]);
  const [usersNoVisits, setUsersNoVisits] = useState([]);
  
  // Table pagination
  const [page, setPage] = useState(1);
  const [allVisits, setAllVisits] = useState([]);
  const [visitorPage, setVisitorPage] = useState(1);
  const [registeredUserPage, setRegisteredUserPage] = useState(1);
  const itemsPerPage = 20;

  // Delete functionality
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState('dateRange'); // 'dateRange', 'month', 'all'
  const [deleteStartDate, setDeleteStartDate] = useState('');
  const [deleteEndDate, setDeleteEndDate] = useState('');
  const [deleteMonth, setDeleteMonth] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'X-Session-Token': sessionToken };

      // Fetch statistics
      const statsRes = await fetch(`/api/admin/site-visits/stats/${timeframe}`, { headers });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStatistics(data.stats);
      }

      // Fetch visits by country
      const countryRes = await fetch(`/api/admin/site-visits/by-country/${timeframe}`, { headers });
      if (countryRes.ok) {
        const data = await countryRes.json();
        setVisitsByCountry(data.data || []);
      }

      // Fetch visits by device
      const deviceRes = await fetch(`/api/admin/site-visits/by-device/${timeframe}`, { headers });
      if (deviceRes.ok) {
        const data = await deviceRes.json();
        setVisitsByDevice(data.data || []);
      }
 
      // Fetch timeline
      const timelineRes = await fetch(`/api/admin/site-visits/timeline/${timeframe}`, { headers });
      if (timelineRes.ok) {
        const data = await timelineRes.json();
        setVisitTimeline(data.data || []);
      }

      // Fetch top referrers
      const referrersRes = await fetch(`/api/admin/site-visits/top-referrers/${timeframe}?limit=5`, { headers });
      if (referrersRes.ok) {
        const data = await referrersRes.json();
        setTopReferrers(data.data || []);
      }

      // Fetch visitor types
      const visitorsRes = await fetch(`/api/admin/site-visits/visitor-types/${timeframe}`, { headers });
      if (visitorsRes.ok) {
        const data = await visitorsRes.json();
        setVisitorTypes(data.data || []);
      }

      // Fetch visitor summary by country
      const visitorSummaryRes = await fetch(`/api/admin/site-visits/visitor-summary/${timeframe}`, { headers });
      if (visitorSummaryRes.ok) {
        const data = await visitorSummaryRes.json();
        setVisitorSummary(data.data || []);
      }

      // Fetch registered users with visit counts
      const registeredUsersRes = await fetch(`/api/admin/site-visits/registered-users/${timeframe}`, { headers });
      if (registeredUsersRes.ok) {
        const data = await registeredUsersRes.json();
        setRegisteredUsersVisits(data.data || []);
      }

      // Fetch registered users with no visits
      const usersNoVisitsRes = await fetch(`/api/admin/site-visits/users-no-visits`, { headers });
      if (usersNoVisitsRes.ok) {
        const data = await usersNoVisitsRes.json();
        setUsersNoVisits(data.data || []);
      }

      // Fetch all visits
      const visitsRes = await fetch(`/api/admin/site-visits?limit=100`, { headers });
      if (visitsRes.ok) {
        const data = await visitsRes.json();
        setAllVisits(data.visits || []);
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeframe]);

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/admin/site-visits/export', {
        headers: { 'X-Session-Token': sessionToken }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `site_visits_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data');
    }
  };

  // Delete visits by date range
  const handleDeleteByDateRange = async () => {
    if (!deleteStartDate || !deleteEndDate) {
      setDeleteError('Please enter both start and end dates');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete all visits between ${deleteStartDate} and ${deleteEndDate}? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(null);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/admin/site-visits/by-date-range', {
        method: 'DELETE',
        headers: {
          'X-Session-Token': sessionToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: deleteStartDate,
          endDate: deleteEndDate
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Delete failed');
      }

      setDeleteSuccess(`Successfully deleted ${data.deletedRows} visits`);
      setDeleteStartDate('');
      setDeleteEndDate('');
      setTimeout(() => {
        fetchAnalytics();
        setShowDeleteModal(false);
      }, 1500);
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete visits');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete visits by month
  const handleDeleteByMonth = async () => {
    if (!deleteMonth) {
      setDeleteError('Please enter a month (YYYY-MM)');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete all visits for ${deleteMonth}? This action cannot be undone.`)) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(null);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/admin/site-visits/by-month', {
        method: 'DELETE',
        headers: {
          'X-Session-Token': sessionToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          yearMonth: deleteMonth
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Delete failed');
      }

      setDeleteSuccess(`Successfully deleted ${data.deletedRows} visits for ${deleteMonth}`);
      setDeleteMonth('');
      setTimeout(() => {
        fetchAnalytics();
        setShowDeleteModal(false);
      }, 1500);
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete visits');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Delete all visits
  const handleDeleteAllVisits = async () => {
    if (!window.confirm('⚠️ PERMANENT ACTION: Delete ALL visits? This cannot be undone!')) {
      return;
    }

    if (!window.confirm('Are you absolutely certain? This will delete the entire visit history.')) {
      return;
    }

    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess(null);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/admin/site-visits/all', {
        method: 'DELETE',
        headers: {
          'X-Session-Token': sessionToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          confirmDelete: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Delete failed');
      }

      setDeleteSuccess(`Successfully deleted ALL ${data.deletedRows} visits`);
      setTimeout(() => {
        fetchAnalytics();
        setShowDeleteModal(false);
      }, 1500);
    } catch (err) {
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete all visits');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Chart data preparations
  const countryChartData = {
    labels: visitsByCountry.map(c => c.country),
    datasets: [{
      label: 'Visits by Country',
      data: visitsByCountry.map(c => c.visit_count),
      backgroundColor: 'rgba(75, 192, 192, 0.6)',
      borderColor: 'rgba(75, 192, 192, 1)',
      borderWidth: 1
    }]
  };

  const timelineChartData = {
    labels: visitTimeline.map(t => t.time_period),
    datasets: [{
      label: 'Total Visits',
      data: visitTimeline.map(t => t.visit_count),
      borderColor: 'rgba(75, 192, 192, 1)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      tension: 0.3,
      fill: true
    }, {
      label: 'Unique Visitors',
      data: visitTimeline.map(t => t.unique_visitors),
      borderColor: 'rgba(153, 102, 255, 1)',
      backgroundColor: 'rgba(153, 102, 255, 0.2)',
      tension: 0.3,
      fill: true
    }]
  };

  const deviceChartData = {
    labels: visitsByDevice.map(d => `${d.device_type} (${d.browser})`),
    datasets: [{
      label: 'Visits by Device',
      data: visitsByDevice.map(d => d.visit_count),
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(75, 192, 192, 0.6)',
        'rgba(153, 102, 255, 0.6)',
      ]
    }]
  };

  const visitorTypeData = {
    labels: visitorTypes.map(v => v.is_returning_visitor ? 'Returning' : 'New'),
    datasets: [{
      data: visitorTypes.map(v => v.visit_count),
      backgroundColor: [
        'rgba(255, 99, 132, 0.6)',
        'rgba(54, 162, 235, 0.6)'
      ]
    }]
  };

  const paginatedVisits = allVisits.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(allVisits.length / itemsPerPage);

  const paginatedVisitors = visitorSummary.slice(
    (visitorPage - 1) * itemsPerPage,
    visitorPage * itemsPerPage
  );

  const totalVisitorPages = Math.ceil(visitorSummary.length / itemsPerPage);

  const paginatedRegisteredUsers = registeredUsersVisits.slice(
    (registeredUserPage - 1) * itemsPerPage,
    registeredUserPage * itemsPerPage
  );

  const totalRegisteredUserPages = Math.ceil(registeredUsersVisits.length / itemsPerPage);

  if (loading && !statistics) {
    return (
      <div className="site-visits-container">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="site-visits-analytics">
      <div className="analytics-header">
        <h2>📊 Site Visits Analytics</h2>
        <div className="header-controls">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="timeframe-select"
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="year">Last Year</option>
          </select>
          <button onClick={exportToCSV} className="btn btn-primary">
            📥 Export to CSV
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="btn btn-danger">
            🗑️ Delete Logs
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗑️ Delete Site Visit Logs</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Error Message */}
              {deleteError && (
                <div className="alert alert-danger">
                  {deleteError}
                </div>
              )}

              {/* Success Message */}
              {deleteSuccess && (
                <div className="alert alert-success">
                  ✅ {deleteSuccess}
                </div>
              )}

              {/* Delete Mode Selection */}
              <div className="delete-mode-selector">
                <label>
                  <input
                    type="radio"
                    value="dateRange"
                    checked={deleteMode === 'dateRange'}
                    onChange={(e) => {
                      setDeleteMode(e.target.value);
                      setDeleteError(null);
                      setDeleteSuccess(null);
                    }}
                  />
                  Delete by Date Range
                </label>
                <label>
                  <input
                    type="radio"
                    value="month"
                    checked={deleteMode === 'month'}
                    onChange={(e) => {
                      setDeleteMode(e.target.value);
                      setDeleteError(null);
                      setDeleteSuccess(null);
                    }}
                  />
                  Delete by Month
                </label>
                <label>
                  <input
                    type="radio"
                    value="all"
                    checked={deleteMode === 'all'}
                    onChange={(e) => {
                      setDeleteMode(e.target.value);
                      setDeleteError(null);
                      setDeleteSuccess(null);
                    }}
                  />
                  Delete All Logs
                </label>
              </div>

              {/* Date Range Inputs */}
              {deleteMode === 'dateRange' && (
                <div className="delete-form-group">
                  <h4>Delete by Date Range</h4>
                  <div className="form-row">
                    <div className="form-field">
                      <label>Start Date:</label>
                      <input
                        type="date"
                        value={deleteStartDate}
                        onChange={(e) => setDeleteStartDate(e.target.value)}
                        placeholder="YYYY-MM-DD"
                      />
                    </div>
                    <div className="form-field">
                      <label>End Date:</label>
                      <input
                        type="date"
                        value={deleteEndDate}
                        onChange={(e) => setDeleteEndDate(e.target.value)}
                        placeholder="YYYY-MM-DD"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Month Input */}
              {deleteMode === 'month' && (
                <div className="delete-form-group">
                  <h4>Delete by Month</h4>
                  <div className="form-field">
                    <label>Month (YYYY-MM):</label>
                    <input
                      type="month"
                      value={deleteMonth}
                      onChange={(e) => setDeleteMonth(e.target.value)}
                      placeholder="2024-07"
                    />
                  </div>
                </div>
              )}

              {/* Delete All Warning */}
              {deleteMode === 'all' && (
                <div className="delete-form-group">
                  <h4>⚠️ Delete All Logs</h4>
                  <p className="warning-text">
                    This will permanently delete ALL site visit logs. This action cannot be undone!
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                onClick={() => setShowDeleteModal(false)}
                className="btn"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteMode === 'dateRange') {
                    handleDeleteByDateRange();
                  } else if (deleteMode === 'month') {
                    handleDeleteByMonth();
                  } else if (deleteMode === 'all') {
                    handleDeleteAllVisits();
                  }
                }}
                disabled={deleteLoading}
                className="btn btn-danger"
              >
                {deleteLoading ? 'Deleting...' : '🗑️ Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Key Metrics */}
      {statistics && (
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-value">{statistics.total_visits || 0}</div>
            <div className="metric-label">Total Visits</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{statistics.unique_visitors || 0}</div>
            <div className="metric-label">Unique Visitors</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{statistics.countries || 0}</div>
            <div className="metric-label">Countries</div>
          </div>
          <div className="metric-card">
            <div className="metric-value">{statistics.device_types || 0}</div>
            <div className="metric-label">Device Types</div>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Timeline Chart */}
        <div className="chart-container full-width">
          <h3>Visits Timeline</h3>
          {visitTimeline.length > 0 ? (
            <Line
              data={timelineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { position: 'top' }
                },
                scales: {
                  y: { beginAtZero: true }
                }
              }}
            />
          ) : (
            <p className="no-data">No data available</p>
          )}
        </div>

        {/* Country Distribution */}
        <div className="chart-container">
          <h3>Visits by Country</h3>
          {visitsByCountry.length > 0 ? (
            <Bar
              data={countryChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                indexAxis: 'y',
                plugins: { legend: { display: false } },
                scales: { x: { beginAtZero: true } }
              }}
            />
          ) : (
            <p className="no-data">No data available</p>
          )}
        </div>

        {/* Device Distribution */}
        <div className="chart-container">
          <h3>Device Distribution</h3>
          {visitsByDevice.length > 0 ? (
            <Doughnut
              data={deviceChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: { legend: { position: 'bottom' } }
              }}
            />
          ) : (
            <p className="no-data">No data available</p>
          )}
        </div>

        {/* Visitor Types */}
        <div className="chart-container">
          <h3>Visitor Types</h3>
          {visitorTypes.length > 0 ? (
            <Pie
              data={visitorTypeData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { position: 'bottom' },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        return `${label}: ${value}`;
                      }
                    }
                  }
                }
              }}
            />
          ) : (
            <p className="no-data">No data available</p>
          )}
        </div>
      </div>

      {/* Top Referrers */}
      {topReferrers.length > 0 && (
        <div className="referrers-section">
          <h3>🔗 Top Referrers</h3>
          <table className="referrers-table">
            <thead>
              <tr>
                <th>Referrer</th>
                <th>Visits</th>
              </tr>
            </thead>
            <tbody>
              {topReferrers.map((referrer, idx) => (
                <tr key={idx}>
                  <td>{referrer.referer || 'Direct'}</td>
                  <td className="visit-count">{referrer.visit_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Visitor Summary by Country */}
      {visitorSummary.length > 0 && (
        <div className="visitor-summary-section">
          <h3>👥 Visitor Visits by Country ({visitorSummary.length} visitors)</h3>
          <div className="table-wrapper">
            <table className="visitor-summary-table">
              <thead>
                <tr>
                  <th>Visitor</th>
                  <th>Country</th>
                  <th>Total Visits</th>
                  <th>First Visit</th>
                  <th>Last Visit</th>
                  <th>Devices Used</th>
                  <th>Browsers Used</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVisitors.map((visitor, idx) => (
                  <tr key={idx} className="visitor-row">
                    <td className="visitor-cell">
                      <span className="visitor-name">
                        {visitor.visitor_email || visitor.visitor_name || 'Anonymous'}
                      </span>
                    </td>
                    <td className="country-cell">
                      <span className="country-badge">{visitor.country || 'Unknown'}</span>
                    </td>
                    <td className="visit-count-cell">
                      <strong>{visitor.visit_count}</strong>
                    </td>
                    <td className="date-cell">
                      {new Date(visitor.first_visit).toLocaleDateString()}
                    </td>
                    <td className="date-cell">
                      {new Date(visitor.last_visit).toLocaleDateString()}
                    </td>
                    <td className="devices-cell">
                      <span className="device-list">
                        {visitor.devices_used ? visitor.devices_used.split(',').join(', ') : 'N/A'}
                      </span>
                    </td>
                    <td className="browsers-cell">
                      <span className="browser-list">
                        {visitor.browsers_used ? visitor.browsers_used.split(',').join(', ') : 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button 
              onClick={() => setVisitorPage(Math.max(1, visitorPage - 1))}
              disabled={visitorPage === 1}
              className="btn btn-sm"
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {visitorPage} of {totalVisitorPages}
            </span>
            <button 
              onClick={() => setVisitorPage(visitorPage + 1)}
              disabled={visitorPage >= totalVisitorPages}
              className="btn btn-sm"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Registered Users Visit Counts */}
      {registeredUsersVisits.length > 0 && (
        <div className="registered-users-section">
          <h3>🔐 Registered Users - Site Visits ({registeredUsersVisits.filter(u => u.visit_count > 0).length} users visited)</h3>
          <div className="table-wrapper">
            <table className="registered-users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Visit Count</th>
                  <th>Countries</th>
                  <th>Devices Used</th>
                  <th>First Visit</th>
                  <th>Last Visit</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRegisteredUsers.map((user, idx) => (
                  <tr key={idx} className={`user-row ${user.visit_count === 0 ? 'no-visits' : ''}`}>
                    <td className="user-name">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="user-email">{user.email}</td>
                    <td className="user-role">
                      <span className={`role-badge role-${user.role}`}>{user.role}</span>
                    </td>
                    <td className="visit-count">
                      <strong className={user.visit_count === 0 ? 'no-visit' : 'has-visit'}>
                        {user.visit_count}
                      </strong>
                    </td>
                    <td className="countries-count">
                      {user.countries_visited > 0 ? (
                        <span className="countries-list">
                          {user.countries_list ? user.countries_list.split(',').join(', ') : 'N/A'}
                        </span>
                      ) : (
                        <span className="no-data-text">-</span>
                      )}
                    </td>
                    <td className="devices-cell">
                      <span className="device-list">
                        {user.devices_used ? user.devices_used.split(',').join(', ') : '-'}
                      </span>
                    </td>
                    <td className="date-cell">
                      {user.first_visit ? new Date(user.first_visit).toLocaleDateString() : '-'}
                    </td>
                    <td className="date-cell">
                      {user.last_visit ? new Date(user.last_visit).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button 
              onClick={() => setRegisteredUserPage(Math.max(1, registeredUserPage - 1))}
              disabled={registeredUserPage === 1}
              className="btn btn-sm"
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {registeredUserPage} of {totalRegisteredUserPages}
            </span>
            <button 
              onClick={() => setRegisteredUserPage(registeredUserPage + 1)}
              disabled={registeredUserPage >= totalRegisteredUserPages}
              className="btn btn-sm"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Users with No Visits Section */}
      {usersNoVisits.length > 0 && (
        <div className="no-visits-section">
          <h3>👤 Registered Users - No Site Visits ({usersNoVisits.length} users)</h3>
          <p className="section-description">These registered users have not visited the site yet.</p>
          <div className="table-wrapper">
            <table className="no-visits-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Account Created</th>
                </tr>
              </thead>
              <tbody>
                {usersNoVisits.slice(0, 10).map((user, idx) => (
                  <tr key={idx}>
                    <td className="user-name">
                      {user.first_name} {user.last_name}
                    </td>
                    <td className="user-email">{user.email}</td>
                    <td className="user-role">
                      <span className={`role-badge role-${user.role}`}>{user.role}</span>
                    </td>
                    <td className="date-cell">
                      {new Date(user.user_created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {usersNoVisits.length > 10 && (
            <p className="more-users">... and {usersNoVisits.length - 10} more users with no visits</p>
          )}
        </div>
      )}

      {/* Detailed Visits Table */}
      <div className="visits-table-section">
        <h3>📋 Detailed Visits ({allVisits.length} total)</h3>
        {paginatedVisits.length > 0 ? (
          <>
            <div className="table-wrapper">
              <table className="visits-table">
                <thead>
                  <tr>
                    <th>Date/Time</th>
                    <th>Visitor</th>
                    <th>Country</th>
                    <th>Device</th>
                    <th>Browser</th>
                    <th>IP Address</th>
                    <th>Referer</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVisits.map((visit, idx) => (
                    <tr key={idx}>
                      <td className="date-cell">
                        {new Date(visit.visit_datetime).toLocaleString()}
                      </td>
                      <td className="visitor-cell">
                        {visit.visitor_email || visit.visitor_name || 'Anonymous'}
                      </td>
                      <td>{visit.country}</td>
                      <td>
                        <span className="badge device-badge">{visit.device_type}</span>
                      </td>
                      <td>
                        {visit.browser} {visit.browser_version && `v${visit.browser_version}`}
                      </td>
                      <td className="ip-cell">{visit.ip_address}</td>
                      <td className="referer-cell" title={visit.referer}>
                        {visit.referer ? visit.referer.substring(0, 30) + '...' : 'Direct'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn btn-sm"
                >
                  ← Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn btn-sm"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="no-data">No visits recorded yet</p>
        )}
      </div>
    </div>
  );
};

export default SiteVisitsAnalytics;
